import InputAdornment from '@mui/material/InputAdornment';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { forwardRef, type ReactNode, type Ref } from 'react';
import type { FieldAppearance } from '../fieldAppearance';

export type SmPickerFieldProps = TextFieldProps & {
  inputRef?: Ref<HTMLInputElement>;
  appearance?: FieldAppearance;
  endIcon?: ReactNode;
};

export const SmPickerField = forwardRef<HTMLDivElement, SmPickerFieldProps>(function SmPickerField(
  {
    label,
    appearance = 'default',
    placeholder,
    disabled,
    error,
    helperText,
    inputRef,
    inputProps,
    InputProps,
    endIcon,
    ...rest
  },
  ref
) {
  const isPlain = appearance === 'plain';
  const resolvedPlaceholder = placeholder;
  const hasLabel = Boolean(label?.toString().trim());
  const inputBaseClassName = ['SmInputBase-root', isPlain ? 'SmInputBase-appearance-plain' : null]
    .filter(Boolean)
    .join(' ');

  return (
    <TextField
      {...rest}
      ref={ref}
      variant="standard"
      label={hasLabel ? label : undefined}
      placeholder={resolvedPlaceholder as string | undefined}
      disabled={disabled}
      error={error}
      helperText={helperText}
      InputLabelProps={
        hasLabel ? { ...(rest.InputLabelProps ?? {}), shrink: true, className: 'SmInputLabel-root' } : undefined
      }
      FormHelperTextProps={{ ...(rest.FormHelperTextProps ?? {}), className: 'SmInputHelperText-root' }}
      InputProps={{
        ...(InputProps ?? {}),
        className: inputBaseClassName,
        disableUnderline: true,
        startAdornment: InputProps?.startAdornment,
        endAdornment:
          InputProps?.endAdornment ??
          (endIcon ? (
            <InputAdornment position="end" className="SmInputAdornment-root">
              {endIcon}
            </InputAdornment>
          ) : undefined)
      }}
      inputProps={{
        ...inputProps,
        ...(isPlain && !inputProps?.['aria-label']
          ? { 'aria-label': (hasLabel ? (label as string) : (resolvedPlaceholder ?? '')) }
          : {})
      } as TextFieldProps['inputProps']}
      inputRef={inputRef}
    />
  );
});
